import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { payslipsAPI, tenantAPI, restaurantAPI } from '../lib/api';
import { toast } from 'sonner';
import { Play, Lock, Eye, Plus, Trash2, FileText, RefreshCw, CheckCircle2, AlertTriangle, MinusCircle, Download } from 'lucide-react';
import PayslipView from '../components/PayslipView';
import LeaveTab from '../components/payroll/LeaveTab';
import DeductionsTab from '../components/payroll/DeductionsTab';
import IncentivesTab from '../components/payroll/IncentivesTab';

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------
const CURRENCY_SYMBOLS = {
  USD: '$', CAD: '$', AUD: 'A$', NZD: 'NZ$', SGD: 'S$', HKD: 'HK$',
  EUR: '€', GBP: '£', PHP: '₱', INR: '₹', BRL: 'R$', MXN: '$', JPY: '¥',
};

const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount));
  } catch {
    const symbol = CURRENCY_SYMBOLS[currency] || currency + ' ';
    return `${symbol}${Number(amount).toFixed(2)}`;
  }
};

const Badge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
    status === 'finalized'
      ? 'bg-teal-50 text-teal-700'
      : 'bg-amber-50 text-amber-700'
  }`}>
    {status}
  </span>
);

const EmailStatusIcon = ({ run }) => {
  if (run.status !== 'finalized') {
    return (
      <span title="Not sent — run not finalized" data-testid="email-status-not-sent">
        <MinusCircle size={16} className="text-slate-300" />
      </span>
    );
  }
  const failed = run.email_summary?.failed ?? 0;
  const sent   = run.email_summary?.sent   ?? 0;
  if (failed > 0) {
    return (
      <span title={`Partial failure — ${sent} sent, ${failed} failed`} data-testid="email-status-partial">
        <AlertTriangle size={16} className="text-amber-500" />
      </span>
    );
  }
  return (
    <span title={`All sent — ${sent} email(s) delivered`} data-testid="email-status-all-sent">
      <CheckCircle2 size={16} className="text-teal-500" />
    </span>
  );
};

// -----------------------------------------------------------------------
// Run Payroll dialog
// -----------------------------------------------------------------------
function RunPayrollDialog({ onRun, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(weekAgo);
  const [end, setEnd]     = useState(today);
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    if (!start || !end || start > end) {
      toast.error('Invalid date range');
      return;
    }
    setRunning(true);
    try {
      await onRun(start, end);
      onClose();
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="run-payroll-dialog">
        <h3 className="font-semibold text-slate-800">Run Payroll</h3>
        <p className="text-xs text-slate-500">Generates payslips for all employees with approved timesheets in the selected period.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Period Start</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="run-payroll-start" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Period End</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="run-payroll-end" />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={handleRun} disabled={running} data-testid="run-payroll-submit-btn"
            className="flex-1 px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2">
            <Play size={14} /> {running ? 'Running…' : 'Run Payroll'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Pay Rules panel
// -----------------------------------------------------------------------
function PayRulesPanel({ rules, onAdd, onDelete }) {
  const [form, setForm] = useState({
    name: '', type: 'deduction', calculation_type: 'percentage',
    value: '', applies_to: 'all', active: true,
  });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.value) return;
    setSaving(true);
    try {
      await onAdd({ ...form, value: parseFloat(form.value) });
      setForm({ name: '', type: 'deduction', calculation_type: 'percentage',
                value: '', applies_to: 'all', active: true });
      setAdding(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">Pay Rules</p>
        <button onClick={() => setAdding(a => !a)} data-testid="add-pay-rule-btn"
          className="flex items-center gap-1 text-xs font-medium text-orange-500 hover:text-orange-600">
          <Plus size={13} /> Add Rule
        </button>
      </div>

      {adding && (
        <div className="border border-slate-100 rounded-lg p-3 space-y-2 bg-slate-50">
          <input placeholder="Rule name (e.g. Tax 10%)" value={form.name}
            onChange={e => setForm(f => ({...f, name: e.target.value}))}
            className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs"
            data-testid="pay-rule-name" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              data-testid="pay-rule-type">
              <option value="earning">Earning</option>
              <option value="deduction">Deduction</option>
            </select>
            <select value={form.calculation_type} onChange={e => setForm(f => ({...f, calculation_type: e.target.value}))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              data-testid="pay-rule-calc-type">
              <option value="percentage">% of pay</option>
              <option value="fixed">Fixed amount</option>
              <option value="multiplier">Multiplier</option>
            </select>
            <input type="number" placeholder="Value" value={form.value}
              onChange={e => setForm(f => ({...f, value: e.target.value}))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              data-testid="pay-rule-value" />
            <select value={form.applies_to} onChange={e => setForm(f => ({...f, applies_to: e.target.value}))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              data-testid="pay-rule-applies-to">
              <option value="all">All pay</option>
              <option value="regular">Regular only</option>
              <option value="overtime">Overtime only</option>
              <option value="holiday">Holiday only</option>
            </select>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Rule'}
          </button>
        </div>
      )}

      {rules.length === 0 && !adding && (
        <p className="text-xs text-slate-400">No pay rules. Add deductions like tax or bonuses.</p>
      )}

      {rules.map(rule => (
        <div key={rule.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
          <div>
            <p className="text-sm text-slate-700">{rule.name}</p>
            <p className="text-xs text-slate-400">
              {rule.type} · {rule.calculation_type} {rule.value}
              {rule.calculation_type === 'percentage' ? '%' : ''} on {rule.applies_to}
            </p>
          </div>
          <button onClick={() => onDelete(rule.id)} data-testid={`delete-rule-${rule.id}`}
            className="p-1.5 text-slate-400 hover:text-red-500 rounded">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Main Page
// -----------------------------------------------------------------------
export default function Payslips() {
  const { user, canManage } = useAuth();
  const [runs, setRuns]               = useState([]);
  const [rules, setRules]             = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);     // run detail
  const [runPayslips, setRunPayslips] = useState([]);
  const [activePayslip, setActivePayslip] = useState(null);  // modal
  const [myPayslips, setMyPayslips]   = useState([]);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [finalizing, setFinalizing]   = useState(null);
  const [resending, setResending]     = useState(false);
  const [filterMonth, setFilterMonth] = useState('all');
  const [tab, setTab]                 = useState(canManage ? 'runs' : 'my');
  const [rplh, setRplh]               = useState(null);   // {total_revenue, staff_hours, value}
  const [revenueInput, setRevenueInput] = useState('');
  const [savingRevenue, setSavingRevenue] = useState(false);
  // RPLH cache: { [runId]: { value, target } } — populated from detail views + background load
  const [rplhCache, setRplhCache] = useState({});
  const [tenantCurrency, setTenantCurrency] = useState('USD');
  const [tenantBrand, setTenantBrand] = useState('');
  const [tenantLogoUrl, setTenantLogoUrl] = useState('');
  const [primaryLocation, setPrimaryLocation] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRun, setDeletingRun] = useState(false);

  const fmt = (n) => formatCurrency(n, tenantCurrency);

  // Load data
  const loadRuns = useCallback(async () => {
    try {
      const r = await payslipsAPI.listRuns();
      const fetchedRuns = r.data?.runs || [];
      setRuns(fetchedRuns);

      // Background-load RPLH for runs that have payslip data (employee_count > 0)
      const targetRuns = fetchedRuns.filter(run => (run.employee_count || 0) > 0);
      if (targetRuns.length > 0) {
        const results = await Promise.allSettled(
          targetRuns.map(run => payslipsAPI.getRunDetail(run.id))
        );
        setRplhCache(prev => {
          const updated = { ...prev };
          results.forEach((res, i) => {
            if (res.status === 'fulfilled' && res.value.data?.rplh) {
              const { value, target } = res.value.data.rplh;
              if (value != null) updated[targetRuns[i].id] = { value, target };
            }
          });
          return updated;
        });
      }
    } catch {}
  }, []);

  const loadMyPayslips = useCallback(async () => {
    try {
      const r = await payslipsAPI.getMyPayslips();
      setMyPayslips(r.data?.payslips || []);
    } catch {}
  }, []);

  const loadPayRules = useCallback(async () => {
    if (!canManage) return;
    try {
      const r = await payslipsAPI.listPayRules();
      setRules(r.data?.rules || []);
    } catch {}
  }, [canManage]);

  const loadTenantSettings = useCallback(async () => {
    try {
      const r = await tenantAPI.getSettings();
      if (r.data?.report_currency) setTenantCurrency(r.data.report_currency);
      if (r.data?.brand_name) setTenantBrand(r.data.brand_name);
      if (r.data?.logo_url) setTenantLogoUrl(r.data.logo_url);
    } catch {}
    try {
      const r = await restaurantAPI.getAll();
      const list = (r.data || []).filter(loc => loc.active !== false);
      if (list.length > 0) setPrimaryLocation(list[0].location_name || '');
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadRuns(), loadMyPayslips(), loadPayRules(), loadTenantSettings()]);
      setLoading(false);
    })();
  }, [loadRuns, loadMyPayslips, loadPayRules, loadTenantSettings]);

  const handleRunPayroll = async (start, end) => {
    try {
      const r = await payslipsAPI.runPayroll({ period_start: start, period_end: end });
      toast.success(`Payroll run created — ${r.data.total} payslip(s) generated`);
      await loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run payroll');
    }
  };

  const handleFinalize = async (runId) => {
    setFinalizing(runId);
    try {
      await payslipsAPI.finalizeRun(runId);
      toast.success('Payroll run finalized');
      await loadRuns();
      if (selectedRun?.id === runId) {
        setSelectedRun(prev => ({ ...prev, status: 'finalized' }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalize');
    } finally {
      setFinalizing(null);
    }
  };

  const handleViewRun = async (run) => {
    try {
      const r = await payslipsAPI.getRunDetail(run.id);
      setSelectedRun(r.data.run);
      setRunPayslips(r.data.payslips || []);
      const rplhData = r.data.rplh || { total_revenue: 0, staff_hours: 0, value: 0 };
      setRplh(rplhData);
      setRevenueInput(rplhData.total_revenue ? String(rplhData.total_revenue) : '');
      // Update cache so the table indicator stays current
      if (rplhData.value != null) {
        setRplhCache(prev => ({
          ...prev,
          [run.id]: { value: rplhData.value, target: rplhData.target },
        }));
      }
    } catch {
      toast.error('Failed to load run details');
    }
  };

  const handleSaveRevenue = async () => {
    if (!selectedRun) return;
    const revenue = parseFloat(revenueInput);
    if (isNaN(revenue) || revenue < 0) {
      toast.error('Enter a valid revenue amount');
      return;
    }
    setSavingRevenue(true);
    try {
      await payslipsAPI.updateRevenue(selectedRun.id, revenue);
      // Re-fetch detail to get updated RPLH
      const r = await payslipsAPI.getRunDetail(selectedRun.id);
      setRplh(r.data.rplh || { total_revenue: 0, staff_hours: 0, value: 0 });
      toast.success('Revenue saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save revenue');
    } finally {
      setSavingRevenue(false);
    }
  };

  const handleAddRule = async (data) => {    try {
      await payslipsAPI.createPayRule(data);
      toast.success('Pay rule added');
      await loadPayRules();
    } catch { toast.error('Failed to save rule'); }
  };

  const handleDeleteRule = async (id) => {
    try {
      await payslipsAPI.deletePayRule(id);
      await loadPayRules();
    } catch { toast.error('Failed to delete rule'); }
  };

  const handleResendFailed = async () => {
    if (!selectedRun) return;
    setResending(true);
    try {
      const r = await payslipsAPI.resendFailed(selectedRun.id);
      const d = r.data;
      if (d.still_failed > 0) {
        toast.warning(d.message || `Resend complete — ${d.resent} sent, ${d.still_failed} still failing`);
      } else {
        toast.success(d.message || `All ${d.resent} payslip(s) resent successfully`);
      }
      // Refresh run detail to show updated email_summary
      const updated = await payslipsAPI.getRunDetail(selectedRun.id);
      setSelectedRun(updated.data.run);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend emails');
    } finally {
      setResending(false);
    }
  };

  const handleDeleteRun = async () => {
    if (!selectedRun) return;
    setDeletingRun(true);
    try {
      await payslipsAPI.deleteRun(selectedRun.id);
      toast.success('Draft payroll deleted');
      setSelectedRun(null);
      setRunPayslips([]);
      setRplh(null);
      setRevenueInput('');
      setShowDeleteConfirm(false);
      await loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payroll run');
    } finally {
      setDeletingRun(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedRun) return;
    try {
      const r = await payslipsAPI.exportCSV(selectedRun.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${selectedRun.period_start}_${selectedRun.period_end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportSummaryPDF = async () => {
    if (!selectedRun) return;
    try {
      const r = await payslipsAPI.exportSummary(selectedRun.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_summary_${selectedRun.period_start}_${selectedRun.period_end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Summary PDF downloaded');
    } catch {
      toast.error('Failed to download summary PDF');
    }
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="payslips-page">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText size={22} className="text-slate-500" />
              Payslips
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Auto-generated from approved timesheets</p>
          </div>
          {canManage && user?.role === 'owner' && (
            <button onClick={() => setShowRunDialog(true)} data-testid="run-payroll-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700">
              <Play size={15} /> Run Payroll
            </button>
          )}
        </div>

        {/* Tab toggle */}
        {canManage && (
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit mb-6 flex-wrap">
            <button onClick={() => setSelectedRun(null) || setTab('runs')}
              data-testid="tab-runs"
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                tab === 'runs' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>Payroll Runs</button>
            <button onClick={() => setTab('my')} data-testid="tab-my"
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                tab === 'my' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>My Payslips</button>
            {user?.role === 'owner' && (
              <button onClick={() => setTab('rules')} data-testid="tab-rules"
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  tab === 'rules' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>Pay Rules</button>
            )}
            <button onClick={() => setTab('leave')} data-testid="tab-leave"
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                tab === 'leave' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>Leave</button>
            <button onClick={() => setTab('deductions')} data-testid="tab-deductions"
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                tab === 'deductions' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>Deductions</button>
            {user?.role === 'owner' && (
              <button onClick={() => setTab('incentives')} data-testid="tab-incentives"
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  tab === 'incentives' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>Incentives</button>
            )}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>
        ) : (
          <>
            {/* ---- Payroll Runs tab ---- */}
            {tab === 'runs' && canManage && !selectedRun && (
              <>
                {runs.length === 0 ? (
                  <div className="text-center py-16 text-slate-400" data-testid="runs-empty-state">
                    <FileText size={32} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No payroll runs yet.</p>
                    {user?.role === 'owner' && (
                      <button onClick={() => setShowRunDialog(true)}
                        className="mt-3 text-sm text-orange-500 hover:text-orange-600 font-medium">
                        Run your first payroll →
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Month filter */}
                    {(() => {
                      const uniqueMonths = [...new Set(
                        runs.map(r => r.period_start?.slice(0, 7)).filter(Boolean)
                      )].sort((a, b) => b.localeCompare(a));
                      const filteredRuns = filterMonth === 'all'
                        ? runs
                        : runs.filter(r => r.period_start?.startsWith(filterMonth));
                      return (
                        <>
                          <div className="flex items-center gap-3 mb-3">
                            <label htmlFor="month-filter"
                              className="text-xs font-medium text-slate-500 whitespace-nowrap">
                              Filter by Month
                            </label>
                            <select
                              id="month-filter"
                              data-testid="month-filter-select"
                              value={filterMonth}
                              onChange={e => setFilterMonth(e.target.value)}
                              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400/30">
                              <option value="all">All</option>
                              {uniqueMonths.map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            {filterMonth !== 'all' && (
                              <button
                                onClick={() => setFilterMonth('all')}
                                data-testid="month-filter-clear"
                                className="text-xs text-slate-400 hover:text-slate-600 underline">
                                Clear
                              </button>
                            )}
                          </div>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[600px] text-sm" data-testid="payroll-runs-table">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Period</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Employees</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredRuns.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400"
                              data-testid="runs-filtered-empty">
                              No runs for {filterMonth}.
                            </td>
                          </tr>
                        ) : filteredRuns.map(run => (
                          <tr key={run.id} data-testid={`run-row-${run.id}`}
                            className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-800">
                              <span className="flex items-center gap-2">
                                {run.period_start} → {run.period_end}
                                {/* RPLH status dot — only when target is set AND RPLH value > 0 */}
                                {(() => {
                                  const cached = rplhCache[run.id];
                                  if (!cached || cached.target == null || cached.value <= 0) return null;
                                  const onTarget = cached.value >= cached.target;
                                  const tooltip = `RPLH: ${formatCurrency(cached.value, tenantCurrency)} (Target: ${formatCurrency(cached.target, tenantCurrency)})`;
                                  return (
                                    <span
                                      className="relative group/rplh flex-shrink-0"
                                      data-testid={`rplh-dot-${run.id}`}
                                      aria-label={tooltip}
                                    >
                                      <span
                                        className={`block w-2.5 h-2.5 rounded-full cursor-default ${
                                          onTarget ? 'bg-green-400' : 'bg-red-400'
                                        }`}
                                      />
                                      {/* Tooltip */}
                                      <span
                                        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/rplh:block
                                                   whitespace-nowrap rounded-md bg-slate-800 text-white text-xs px-2.5 py-1.5 shadow-lg z-50"
                                      >
                                        {tooltip}
                                        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                                      </span>
                                    </span>
                                  );
                                })()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {run.employee_count ?? 0} employee(s)
                            </td>
                            <td className="px-4 py-3">
                              <Badge status={run.status} />
                            </td>
                            <td className="px-4 py-3 text-center" data-testid={`email-status-cell-${run.id}`}>
                              <EmailStatusIcon run={run} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <button onClick={() => { setTab('runs'); handleViewRun(run); }}
                                  data-testid={`view-run-${run.id}`}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50">
                                  <Eye size={13} /> View
                                </button>
                                {run.status !== 'finalized' && (user?.role === 'owner' || user?.role === 'manager') && (
                                  <button onClick={() => handleFinalize(run.id)}
                                    disabled={finalizing === run.id}
                                    data-testid={`finalize-run-${run.id}`}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
                                    <Lock size={13} />
                                    {finalizing === run.id ? 'Locking…' : 'Finalize'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </>
            )}

            {/* ---- Run Detail (payslips for a run) ---- */}
            {tab === 'runs' && canManage && selectedRun && (
              <div>
                <button onClick={() => { setSelectedRun(null); setRplh(null); setRevenueInput(''); }}
                  className="text-sm text-slate-500 hover:text-slate-700 mb-4 flex items-center gap-1"
                  data-testid="back-to-runs-btn">
                  ← Back to runs
                </button>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-semibold text-slate-800">
                      {selectedRun.period_start} → {selectedRun.period_end}
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {runPayslips.length} payslip(s) · <Badge status={selectedRun.status} />
                    </p>
                  </div>
                <div className="flex items-center gap-2">
                  {selectedRun.status !== 'finalized' && (user?.role === 'owner' || user?.role === 'manager') && (
                    <button onClick={() => handleFinalize(selectedRun.id)}
                      disabled={finalizing === selectedRun.id}
                      data-testid="finalize-run-detail-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
                      <Lock size={13} /> Finalize Run
                    </button>
                  )}
                  {selectedRun.status === 'draft' && canManage && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      data-testid="delete-draft-run-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100">
                      <Trash2 size={13} /> Delete Draft
                    </button>
                  )}
                  {selectedRun.status === 'finalized' && (
                    <p className="text-xs text-slate-400 hidden sm:block" data-testid="finalized-no-delete-hint">
                      Finalized payroll cannot be deleted
                    </p>
                  )}
                  {selectedRun.status === 'finalized' &&
                   (selectedRun.email_summary?.failed ?? 0) > 0 &&
                   canManage && (
                    <button
                      onClick={handleResendFailed}
                      disabled={resending}
                      data-testid="resend-failed-emails-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                      <RefreshCw size={13} className={resending ? 'animate-spin' : ''} />
                      {resending ? 'Resending…' : `Resend Failed (${selectedRun.email_summary?.failed})`}
                    </button>
                  )}
                  {selectedRun.status === 'finalized' && canManage && (
                    <button
                      onClick={handleExportCSV}
                      data-testid="export-csv-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50">
                      <Download size={13} /> Export CSV
                    </button>
                  )}
                  {selectedRun.status === 'finalized' && canManage && (
                    <button
                      onClick={handleExportSummaryPDF}
                      data-testid="export-summary-pdf-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700">
                      <FileText size={13} /> Download Summary PDF
                    </button>
                  )}
                </div>
                </div>

                {/* ── RPLH Card ──────────────────────────────────────────── */}
                {rplh !== null && (
                  <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="rplh-card">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                      {/* Revenue input */}
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                          Total Sales <span className="normal-case font-normal text-slate-400">(used to calculate RPLH)</span>
                        </label>
                        {selectedRun?.status === 'draft' ? (
                          <div className="flex gap-2 flex-wrap">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{CURRENCY_SYMBOLS[tenantCurrency] || tenantCurrency}</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={revenueInput}
                                onChange={e => setRevenueInput(e.target.value)}
                                placeholder="0.00"
                                data-testid="revenue-input"
                                className="pl-7 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-40 focus:outline-none focus:ring-2 focus:ring-orange-300"
                              />
                            </div>
                            <button
                              onClick={handleSaveRevenue}
                              disabled={savingRevenue}
                              data-testid="save-revenue-btn"
                              className="px-4 py-2 text-xs font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                            >
                              {savingRevenue ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        ) : (
                          <p className="text-sm font-semibold text-slate-800" data-testid="revenue-readonly">
                            {rplh.total_revenue ? `$${rplh.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                          </p>
                        )}
                        {/* Helper text */}
                        <p className="text-xs text-slate-400 mt-1.5 leading-relaxed max-w-xs">
                          {selectedRun?.status === 'draft'
                            ? revenueInput
                              ? 'RPLH updates automatically after saving.'
                              : 'Enter your total sales for this period. RPLH = Sales ÷ Staff Hours (excluding management).'
                            : 'Revenue locked — run is finalized.'}
                        </p>
                      </div>

                      {/* RPLH metrics */}
                      <div className="flex items-center gap-6 sm:border-l sm:border-slate-200 sm:pl-6">
                        <div className="text-center" data-testid="rplh-staff-hours">
                          <p className="text-xs text-slate-500 font-medium">Staff Hours</p>
                          <p className="text-lg font-bold text-slate-800">{rplh.staff_hours}h</p>
                        </div>
                        <div className="text-center" data-testid="rplh-value">
                          <p className="text-xs text-slate-500 font-medium">RPLH</p>
                          <p className={`text-lg font-bold ${rplh.value > 0 ? 'text-teal-600' : 'text-slate-400'}`}>
                            {rplh.value > 0
                              ? formatCurrency(rplh.value, tenantCurrency)
                              : <span className="text-sm font-normal" data-testid="rplh-empty-hint">Enter total sales to calculate RPLH</span>}
                          </p>
                          {/* Target comparison badge */}
                          {rplh.target != null && rplh.value > 0 && (() => {
                            const onTarget = rplh.value >= rplh.target;
                            return (
                              <div className="mt-1 space-y-0.5">
                                <p className="text-xs text-slate-400">
                                  Target: {formatCurrency(rplh.target, tenantCurrency)}
                                </p>
                                <span
                                  data-testid={onTarget ? 'rplh-on-target' : 'rplh-below-target'}
                                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                                    onTarget
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {onTarget ? '✓ On Target' : '↓ Below Target'}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {runPayslips.length === 0 ? (
                  <p className="text-sm text-slate-400 py-8 text-center">
                    No payslips — no approved timesheets in this period.
                  </p>
                ) : (                  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[540px] text-sm" data-testid="run-payslips-table">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Employee</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Hours</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Gross</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Net</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">View</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {runPayslips.map(ps => (
                          <tr key={ps.id} data-testid={`payslip-row-${ps.id}`}
                            className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-800">{ps.employee_name}</td>
                            <td className="px-4 py-3 text-right text-slate-600">{ps.total_hours}h</td>
                            <td className="px-4 py-3 text-right font-medium text-slate-800">{fmt(ps.gross_pay)}</td>
                            <td className="px-4 py-3 text-right font-bold text-teal-600">{fmt(ps.net_pay)}</td>
                            <td className="px-4 py-3 text-right">
                              <button onClick={() => setActivePayslip(ps)}
                                data-testid={`view-payslip-${ps.id}`}
                                className="text-orange-500 hover:text-orange-600 text-xs font-medium">
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ---- My Payslips tab ---- */}
            {tab === 'my' && (
              <>
                {myPayslips.length === 0 ? (
                  <div className="text-center py-16 text-slate-400" data-testid="my-payslips-empty">
                    <FileText size={32} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No payslips yet.</p>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[480px] text-sm" data-testid="my-payslips-table">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Period</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Hours</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Gross</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Net</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">View</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {myPayslips.map(ps => (
                          <tr key={ps.id} data-testid={`my-payslip-row-${ps.id}`}
                            className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-800">
                              {ps.period_start} → {ps.period_end}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">{ps.total_hours}h</td>
                            <td className="px-4 py-3 text-right font-medium text-slate-800">{fmt(ps.gross_pay)}</td>
                            <td className="px-4 py-3 text-right font-bold text-teal-600">{fmt(ps.net_pay)}</td>
                            <td className="px-4 py-3 text-right">
                              <button onClick={() => setActivePayslip(ps)}
                                data-testid={`view-my-payslip-${ps.id}`}
                                className="text-orange-500 hover:text-orange-600 text-xs font-medium">
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ---- Pay Rules tab ---- */}
            {tab === 'rules' && user?.role === 'owner' && (
              <PayRulesPanel rules={rules} onAdd={handleAddRule} onDelete={handleDeleteRule} />
            )}

            {/* ---- Leave tab ---- */}
            {tab === 'leave' && <LeaveTab />}

            {/* ---- Deductions tab ---- */}
            {tab === 'deductions' && canManage && <DeductionsTab />}

            {/* ---- Incentives tab ---- */}
            {tab === 'incentives' && user?.role === 'owner' && <IncentivesTab />}
          </>
        )}
      </div>

      {/* Dialogs */}
      {showRunDialog && (
        <RunPayrollDialog onRun={handleRunPayroll} onClose={() => setShowRunDialog(false)} />
      )}
      {activePayslip && (
        <PayslipView payslip={activePayslip} currency={tenantCurrency} brandName={tenantBrand} location={primaryLocation} logoUrl={tenantLogoUrl} onClose={() => setActivePayslip(null)} />
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="delete-confirm-modal">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Delete Draft Payroll?</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedRun?.period_start} → {selectedRun?.period_end}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Delete this draft payroll? This will permanently remove all generated payslips.
            </p>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingRun}
                className="flex-1 px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={handleDeleteRun}
                disabled={deletingRun}
                data-testid="confirm-delete-run-btn"
                className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                <Trash2 size={14} />
                {deletingRun ? 'Deleting…' : 'Delete Payroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
